/**
 * GRI Questionnaire Main Component
 *
 * Main page for GRI questionnaire management.
 * Features:
 * - Category tabs (Environment, Social, Governance)
 * - Standards accordions with progress tracking
 * - Disclosure panels with questions
 * - Submit/approve/reject workflow
 * - Export functionality
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  Tabs,
  Tab,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  ExpandMore,
  Nature,
  People,
  AccountBalance,
  CheckCircle,
  Send,
  CloudDownload,
  Refresh,
  Assignment,
} from '@mui/icons-material';
import {
  griService,
  GRICategory,
  GRIStandard,
  GRISession,
  SessionProgress,
} from '../../services/griService';
import { GRIDisclosurePanel } from './GRIDisclosurePanel';
import { GRIProgressSidebar } from './GRIProgressSidebar';
import { useApp } from '../../contexts/AuthContext';

interface CategoryTab {
  value: GRICategory;
  label: string;
  icon: React.ReactElement;
}

const CATEGORY_TABS: CategoryTab[] = [
  { value: 'environment', label: 'Environment', icon: <Nature /> },
  { value: 'social', label: 'Social', icon: <People /> },
  { value: 'governance', label: 'Governance', icon: <AccountBalance /> },
];

export const GRIQuestionnaire: React.FC = () => {
  // Get selected period from global app context (linked to header year selector)
  const { selectedPeriod } = useApp();
  const reportingPeriod = selectedPeriod?.year?.toString() || '2024';

  // State
  const [activeCategory, setActiveCategory] = useState<GRICategory>('environment');
  const [session, setSession] = useState<GRISession | null>(null);
  const [progress, setProgress] = useState<SessionProgress | null>(null);
  const [standards, setStandards] = useState<GRIStandard[]>([]);
  const [expandedStandard, setExpandedStandard] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [submitNotes, setSubmitNotes] = useState('');
  const [availableCategories, setAvailableCategories] = useState<Set<string>>(new Set(['environment']));
  const [noStandardsConfigured, setNoStandardsConfigured] = useState(false);

  // Detect available categories from all standards on mount
  useEffect(() => {
    const detectCategories = async () => {
      try {
        const allStandards = await griService.standards.list();
        if (allStandards.length === 0) {
          setNoStandardsConfigured(true);
          return;
        }
        setNoStandardsConfigured(false);
        const categories = new Set(allStandards.map((s) => s.category));
        setAvailableCategories(categories);
      } catch {
        // If fetching all standards fails, keep default (environment only)
      }
    };
    detectCategories();
  }, []);

  // Initialize session and load data
  const initializeSession = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Create or get existing session
      const sessionResult = await griService.sessions.create(activeCategory, reportingPeriod);

      // Load session details, progress, and standards in parallel
      const [sessionData, progressData, standardsData] = await Promise.all([
        griService.sessions.get(sessionResult.session_id),
        griService.sessions.getProgress(sessionResult.session_id),
        griService.standards.list(activeCategory),
      ]);

      setSession(sessionData);
      setProgress(progressData);
      setStandards(standardsData);

      // Auto-expand first standard if none expanded
      if (!expandedStandard && standardsData.length > 0) {
        setExpandedStandard(standardsData[0].id);
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load questionnaire';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [activeCategory, reportingPeriod, expandedStandard]);

  useEffect(() => {
    initializeSession();
  }, [activeCategory, reportingPeriod]); // Removed initializeSession from deps to avoid infinite loop

  // Refresh progress after response saved
  const handleResponseSaved = useCallback(async () => {
    if (!session) return;
    try {
      const progressData = await griService.sessions.getProgress(session.id);
      setProgress(progressData);
    } catch (err) {
      console.error('Failed to refresh progress:', err);
    }
  }, [session]);

  // Handle submit
  const handleSubmit = async () => {
    if (!session) return;
    setSubmitting(true);
    try {
      await griService.sessions.submit(session.id, submitNotes || undefined);
      setShowSubmitDialog(false);
      setSubmitNotes('');
      await initializeSession(); // Refresh
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit questionnaire';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle export
  const handleExport = async () => {
    if (!session) return;
    try {
      await griService.sessions.downloadExcel(
        session.id,
        `gri_${activeCategory}_${reportingPeriod}.xlsx`
      );
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to export';
      setError(errorMessage);
    }
  };

  // Handle category change
  const handleCategoryChange = (_event: React.SyntheticEvent, newValue: GRICategory) => {
    if (availableCategories.has(newValue)) {
      setActiveCategory(newValue);
      setExpandedStandard(null);
    }
  };

  // Handle standard click from sidebar
  const handleStandardClick = (standardCode: string) => {
    const standard = standards.find((s) => s.code === standardCode);
    if (standard) {
      setExpandedStandard(standard.id);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const isEditable = session?.status === 'draft' || session?.status === 'rejected';

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 0.5 }}>
            GRI Questionnaire
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b7280' }}>
            Complete GRI disclosures for sustainability reporting
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Tooltip title="Refresh">
            <IconButton onClick={initializeSession} size="small">
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<CloudDownload />}
            onClick={handleExport}
            disabled={!session}
            size="small"
          >
            Export Excel
          </Button>
          {isEditable && (
            <Button
              variant="contained"
              startIcon={<Send />}
              onClick={() => setShowSubmitDialog(true)}
              disabled={!session}
              size="small"
            >
              Submit for Review
            </Button>
          )}
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* No Standards Alert */}
      {noStandardsConfigured && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          No GRI standards are configured. Please contact your administrator to seed the GRI standards database.
        </Alert>
      )}

      {/* Category Tabs */}
      <Card sx={{ mb: 3, border: '1px solid #e5e7eb', boxShadow: 'none' }}>
        <Tabs
          value={activeCategory}
          onChange={handleCategoryChange}
          sx={{ borderBottom: '1px solid #e5e7eb' }}
        >
          {CATEGORY_TABS.map((tab) => {
            const isAvailable = availableCategories.has(tab.value);
            return (
              <Tab
                key={tab.value}
                value={tab.value}
                label={isAvailable ? tab.label : `${tab.label} (Coming Soon)`}
                icon={tab.icon}
                disabled={!isAvailable}
                iconPosition="start"
                sx={{ textTransform: 'none' }}
              />
            );
          })}
        </Tabs>

        {/* Overall Progress */}
        {progress && (
          <Box sx={{ p: 2, backgroundColor: '#f9fafb' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Overall Progress
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#3b82f6' }}>
                {progress.answered_questions}/{progress.total_questions} (
                {progress.completion_percentage.toFixed(0)}%)
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress.completion_percentage}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: '#e5e7eb',
                '& .MuiLinearProgress-bar': {
                  backgroundColor:
                    progress.completion_percentage === 100 ? '#059669' : '#3b82f6',
                },
              }}
            />
          </Box>
        )}
      </Card>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Standards Accordions */}
        <Grid item xs={12} md={9}>
          {standards.map((standard) => {
            const standardProgress = progress?.standards_progress.find(
              (s) => s.standard_code === standard.code
            );
            const percentage = standardProgress?.percentage || 0;
            const isComplete = percentage === 100;

            return (
              <Accordion
                key={standard.id}
                expanded={expandedStandard === standard.id}
                onChange={() =>
                  setExpandedStandard(expandedStandard === standard.id ? null : standard.id)
                }
                sx={{
                  mb: 2,
                  border: '1px solid #e5e7eb',
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      width: '100%',
                      pr: 2,
                    }}
                  >
                    {isComplete ? (
                      <CheckCircle sx={{ color: '#059669', fontSize: 20 }} />
                    ) : (
                      <Assignment sx={{ color: '#6b7280', fontSize: 20 }} />
                    )}
                    <Chip
                      label={standard.code}
                      size="small"
                      sx={{
                        backgroundColor: isComplete ? '#dcfce7' : '#dbeafe',
                        color: isComplete ? '#166534' : '#1e40af',
                        fontWeight: 600,
                      }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#111827' }}>
                        {standard.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6b7280' }}>
                        {standard.version_year} • {standard.disclosure_count} disclosures
                      </Typography>
                    </Box>
                    {standardProgress && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
                        <LinearProgress
                          variant="determinate"
                          value={percentage}
                          sx={{
                            width: 100,
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: '#e5e7eb',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: isComplete ? '#059669' : '#3b82f6',
                            },
                          }}
                        />
                        <Typography variant="caption" sx={{ minWidth: 40, color: '#6b7280' }}>
                          {percentage.toFixed(0)}%
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {expandedStandard === standard.id && session && (
                    <GRIDisclosurePanel
                      standardId={standard.id}
                      sessionId={session.id}
                      isEditable={isEditable}
                      onResponseSaved={handleResponseSaved}
                    />
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Grid>

        {/* Progress Sidebar */}
        <Grid item xs={12} md={3}>
          {progress && (
            <GRIProgressSidebar
              progress={progress}
              session={session}
              onStandardClick={handleStandardClick}
            />
          )}
        </Grid>
      </Grid>

      {/* Submit Dialog */}
      <Dialog open={showSubmitDialog} onClose={() => setShowSubmitDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Submit Questionnaire for Review</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#6b7280' }}>
            Once submitted, the questionnaire will be reviewed by an administrator or ESG manager.
            You won't be able to make changes until it's approved or rejected.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes (optional)"
            value={submitNotes}
            onChange={(e) => setSubmitNotes(e.target.value)}
            placeholder="Add any notes for the reviewer..."
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSubmitDialog(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : <Send />}
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GRIQuestionnaire;
